/**
 * Collection à onglets (sections/max-onglets-produits.liquid).
 *
 * - Une pastille blanche glisse sous l'onglet actif (largeur + position animées en CSS).
 * - Changer d'onglet fait sortir les cartes d'un côté puis entrer les nouvelles de l'autre ;
 *   le sens suit le clic (vers la droite ou vers la gauche).
 * - Mobile : le carrousel au doigt (scroll-snap) a des points de position.
 * - Éditeur de thème : sélectionner un block affiche son onglet.
 *
 * Même convention que max-hero-boucle : `data-tab` sur les boutons, `data-panel` sur les panneaux.
 */
const LEAVE_MS = 280;

class MaxOngletsProduits extends HTMLElement {
  connectedCallback() {
    this.tabsEl = this.querySelector('.mop__tabs');
    if (!this.tabsEl) return;
    this.indicator = this.tabsEl.querySelector('.mop__indicator');
    this.tabs = [...this.tabsEl.querySelectorAll('[data-tab]')];
    this.panels = [...this.querySelectorAll('[data-panel]')];
    this.current = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
    this.reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.tabsEl.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-tab]');
      if (tab) this.select(this.tabs.indexOf(tab));
    });

    // Flèches gauche / droite entre les onglets (motif ARIA « tabs »).
    this.tabsEl.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      const step = event.key === 'ArrowRight' ? 1 : -1;
      const next = (this.current + step + this.tabs.length) % this.tabs.length;
      this.select(next);
      this.tabs[next].focus();
    });

    this.addEventListener('shopify:block:select', (event) => {
      const tab = event.target.closest('[data-tab]');
      if (tab) this.select(this.tabs.indexOf(tab), { instant: true });
    });

    this.panels.forEach((panel) => this.setupDots(panel));

    this.moveIndicator();
    // La transition ne s'active qu'après le premier placement : pas de glissement au chargement.
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-ready')));

    this.onResize = () => this.moveIndicator();
    window.addEventListener('resize', this.onResize);
    if (document.fonts) document.fonts.ready.then(() => this.moveIndicator());
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.timer);
  }

  moveIndicator() {
    const tab = this.tabs[this.current];
    if (!tab || !this.indicator) return;
    this.indicator.style.width = `${tab.offsetWidth}px`;
    this.indicator.style.transform = `translateX(${tab.offsetLeft}px)`;
  }

  select(index, { instant = false } = {}) {
    if (index < 0 || index === this.current) return;
    const dir = index > this.current ? 1 : -1;
    const from = this.panels[this.current];
    const to = this.panels[index];
    this.current = index;

    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });
    this.moveIndicator();
    this.centerTab(this.tabs[index]);

    const show = () => {
      if (from) from.hidden = true;
      if (from) from.classList.remove('is-leaving');
      if (!to) return;
      to.style.setProperty('--mop-dir', dir);
      to.classList.add('is-entering');
      to.hidden = false;
      const list = to.querySelector('.mop__list');
      if (list) list.scrollLeft = 0;
      this.updateDots(to);
      to.offsetWidth; // force le reflow pour rejouer l'entrée
      to.classList.remove('is-entering');
    };

    clearTimeout(this.timer);
    if (instant || this.reduce || !from) return show();
    from.style.setProperty('--mop-dir', dir);
    from.classList.add('is-leaving');
    this.timer = setTimeout(show, LEAVE_MS);
  }

  // Mobile : si la barre d'onglets déborde, on la fait défiler pour centrer l'onglet choisi.
  centerTab(tab) {
    const bar = this.tabsEl;
    if (bar.scrollWidth <= bar.clientWidth) return;
    bar.scrollTo({
      left: tab.offsetLeft - (bar.clientWidth - tab.offsetWidth) / 2,
      behavior: this.reduce ? 'auto' : 'smooth',
    });
  }

  // Points du carrousel mobile : une position par carte, moins celles déjà visibles (2 par vue).
  setupDots(panel) {
    const list = panel.querySelector('.mop__list');
    const dots = panel.querySelector('.mop__dots');
    if (!list || !dots) return;
    const count = Math.max(1, list.children.length - 1);
    dots.innerHTML = '<span></span>'.repeat(count);
    list.addEventListener('scroll', () => requestAnimationFrame(() => this.updateDots(panel)), { passive: true });
    this.updateDots(panel);
  }

  updateDots(panel) {
    const list = panel.querySelector('.mop__list');
    const dots = panel.querySelector('.mop__dots');
    if (!list || !dots || !list.children.length) return;
    const first = list.children[0];
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const step = first.offsetWidth + gap;
    const spans = [...dots.children];
    const i = Math.min(spans.length - 1, Math.round(list.scrollLeft / (step || 1)));
    spans.forEach((dot, k) => dot.classList.toggle('is-active', k === i));
  }
}

if (!customElements.get('max-onglets-produits')) customElements.define('max-onglets-produits', MaxOngletsProduits);
